import type { IPredicate, IPredicateGroup } from '@feathr/blackbox';

import type { TPersonStatus } from './PersonStatusSegmentedControl';

type TPredicate = IPredicate | IPredicateGroup;

// Predicate group that includes both 'mode' (for TypeScript) and 'group_mode' (for backend)
interface IPredicateGroupWithBackendField extends IPredicateGroup {
  group_mode: 'match_any' | 'match_all';
}

export interface IMergedPredicateResult {
  predicates: TPredicate[];
  mode: 'match_all' | 'match_any';
}

const PERSON_STATUS_ATTRIBUTE = 'is_archived';

/**
 * Creates the necessary predicates to select persons based on their status
 *
 * Because the person may not have an archived attribute yet, we have to check for whether it exists in addition to if it is false
 * @param status whether the status active or inactive.
 * @returns A Predicate that filters based on archival status.
 */
export function getPersonStatusPredicate(status: TPersonStatus): TPredicate {
  const isArchived = status === 'archived';
  const archivedPredicate: IPredicate = {
    attr_against: PERSON_STATUS_ATTRIBUTE,
    attr_type: 'boolean',
    comparison: 'eq',
    kind: 'attribute',
    value: isArchived,
  };
  if (isArchived) {
    return archivedPredicate;
  }
  return {
    group_mode: 'match_any',
    kind: 'attribute',
    group: [
      archivedPredicate,
      {
        attr_against: PERSON_STATUS_ATTRIBUTE,
        attr_type: 'boolean',
        comparison: 'nexists',
        kind: 'attribute',
      },
    ],
  };
}

// Defaults to the active status predicate so new filter stacks behave as before the toggle existed.
export function getDefaultPersonStatusPredicate(): TPredicate {
  return getPersonStatusPredicate('active');
}

/**
 * Adds an is_archived filter to existing segment predicates at the top level.
 * This ensures is_archived (person field) is separate from activity filters.
 */
export function mergePersonStatusPredicate(
  predicates: TPredicate[],
  status: TPersonStatus,
  segmentMode?: 'match_all' | 'match_any',
): TPredicate[] {
  const newPredicate = getPersonStatusPredicate(status);

  // If segment has no predicates, just return the is_archived filter
  if (predicates.length === 0) {
    return [newPredicate];
  }

  // If segment has multiple predicates with match_any mode,
  // we need to wrap them to preserve that logic: (pred1 OR pred2) AND is_archived
  // The is_archived predicate for active status is already a group with OR logic,
  // so we need an extra layer to ensure proper nesting
  if (segmentMode === 'match_any' && predicates.length > 1) {
    const wrappedSegmentPredicates: IPredicateGroupWithBackendField = {
      kind: 'mixed',
      group: predicates,
      mode: 'match_any',
      group_mode: 'match_any',
    };

    // Wrap both the segment predicates AND the status predicate in a match_all group
    // This ensures: (segment_pred1 OR segment_pred2) AND (is_archived=false OR nexists)
    const combinedGroup: IPredicateGroupWithBackendField = {
      kind: 'mixed',
      group: [wrappedSegmentPredicates, newPredicate],
      mode: 'match_all',
      group_mode: 'match_all',
    };

    return [combinedGroup];
  }

  // For single predicates or match_all mode, just append is_archived
  // The caller should set mode to match_all to ensure proper ANDing
  return [...predicates, newPredicate];
}

// Shorthand for callers that always fall back to the active-person filter.
export function mergeDefaultPersonStatusPredicate(predicates: TPredicate[]): TPredicate[] {
  return mergePersonStatusPredicate(predicates, 'active');
}
